// synced from loomweb blocks/shared/tool-result-panel.tsx (de-Tailwind'd).
/**
 * Collapsible tool-result output region shared by the command / file-edit /
 * file-search views. Faithful to loomweb's ToolResultPanel: a toggle row (chevron
 * + title), an optional exit-code / status badge, a copy button, and a max-height
 * scrollable <pre> body. Tailwind classes are replaced by namespaced `replay-trp-*`
 * CSS; lucide chevrons by the local inline-SVG set.
 */
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "./icons";
import { CopyButton } from "./copy-button";

export type ToolResultStatus = "completed" | "error" | "streaming" | "in_progress" | "waiting";

export interface ToolResultPanelProps {
  /** Title (e.g. Output / Results). */
  title: string;
  /** Result body (plain text, multi-line). */
  content: string;
  /** aria-label for the copy button. */
  copyAriaLabel: string;
  /** Initial collapsed state (default true). */
  defaultCollapsed?: boolean;
  /** Exit code; when present it is shown in preference to a status badge. */
  exitCode?: number;
  /** Status, used to show a completed/failed badge when there is no exit code. */
  status?: ToolResultStatus;
  /** Max height of the body before it scrolls (default 240px). */
  maxHeightPx?: number;
}

export function ToolResultPanel({
  title,
  content,
  copyAriaLabel,
  defaultCollapsed = true,
  exitCode,
  status,
  maxHeightPx = 240
}: ToolResultPanelProps): JSX.Element | null {
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);

  const statusBadge = useMemo(() => {
    if (exitCode !== undefined) {
      return {
        text: `exit ${exitCode}`,
        className: exitCode === 0 ? "replay-text-success" : "replay-text-error"
      };
    }
    if (status === "completed") return { text: "completed", className: "replay-text-success" };
    if (status === "error") return { text: "failed", className: "replay-text-error" };
    return null;
  }, [exitCode, status]);

  if (content.length === 0) return null;

  return (
    <div className="replay-trp">
      <div className="replay-trp-head">
        <button type="button" className="replay-trp-toggle" onClick={() => setCollapsed((v) => !v)}>
          {collapsed ? (
            <ChevronDownIcon className="replay-icon-xs" />
          ) : (
            <ChevronUpIcon className="replay-icon-xs" />
          )}
          {title}
        </button>
        {statusBadge !== null && (
          <span className={`replay-trp-badge ${statusBadge.className}`}>{statusBadge.text}</span>
        )}
        <CopyButton text={content} className="replay-ml-auto" ariaLabel={copyAriaLabel} />
      </div>
      {!collapsed && (
        <pre className="replay-trp-body" style={{ maxHeight: `${maxHeightPx}px` }}>
          {content}
        </pre>
      )}
    </div>
  );
}
